/**
 * Dirty-form guard (W1 S2b).
 *
 * Any control that changes scope (the ScopeRibbon's environment selector
 * today; a breadcrumb parent-navigation click later) must not silently
 * discard an unsaved form on the current page. This is deliberately NOT a
 * React Context: a page with a form and the shell control that might
 * navigate away from it are siblings under Layout, not parent/child, and
 * only one page (hence one form) is ever mounted at a time in this SPA — a
 * small module-level flag, consulted only at the moment a guarded action is
 * attempted, is simpler than wiring a Provider through Layout for a value
 * nothing needs to re-render on.
 *
 * Usage:
 *   // in the page/form that can go dirty:
 *   const { markDirty, markClean } = useDirtyForm();
 *   useEffect(() => { if (hasUnsavedChanges) markDirty(); else markClean(); }, [hasUnsavedChanges]);
 *
 *   // in anything that changes scope/navigates away (e.g. ScopeRibbon):
 *   const guard = useDirtyGuard();
 *   guard(() => setEnvironmentId('test'));
 */
import { useCallback, useEffect } from 'react';

const DEFAULT_MESSAGE = 'You have unsaved changes. Leave without saving?';

let dirty = false;
let dirtyMessage = DEFAULT_MESSAGE;

/** Test-only escape hatch: reset the module-level flag between test cases. */
export function _resetDirtyGuardForTests(): void {
  dirty = false;
  dirtyMessage = DEFAULT_MESSAGE;
}

export function isDirty(): boolean {
  return dirty;
}

/** For a page/form: register that it now has (or no longer has) unsaved state. */
export function setDirty(next: boolean, message: string = DEFAULT_MESSAGE): void {
  dirty = next;
  if (next) dirtyMessage = message;
}

/**
 * Hook for a page/form with local unsaved state. Automatically clears the
 * flag on unmount so navigating away by any other route (not just a guarded
 * action) never leaves a stale block behind for the next page.
 */
export function useDirtyForm(message: string = DEFAULT_MESSAGE): {
  markDirty: () => void;
  markClean: () => void;
} {
  useEffect(() => () => setDirty(false), []);

  const markDirty = useCallback(() => setDirty(true, message), [message]);
  const markClean = useCallback(() => setDirty(false), []);

  return { markDirty, markClean };
}

/**
 * Hook for anything that performs a scope/navigation change and must not do
 * so silently while a form is dirty. `confirmFn` defaults to the browser's
 * native confirm() and is overridable for tests / a future custom dialog.
 * Confirming clears the flag (the user chose to leave) before running the
 * guarded action; declining runs nothing.
 */
export function useDirtyGuard(
  confirmFn: (message: string) => boolean = (message) => window.confirm(message),
): (action: () => void) => void {
  return useCallback(
    (action: () => void) => {
      if (isDirty() && !confirmFn(dirtyMessage)) return;
      setDirty(false);
      action();
    },
    [confirmFn],
  );
}
